/**
 * Cart 2.0 — <fit-text>
 *
 * Shrinks its own font-size just enough to keep the text on a single line within the
 * available width. Used for Complete Your Routine recommendation titles so long product
 * names (e.g. "The Ultimate Body Bar Kit") stay one line instead of wrapping.
 *
 * The starting (largest) size is whatever CSS gives the element; it only ever shrinks,
 * down to a floor, and never grows past the CSS size. Re-fits when webfonts finish
 * loading and on viewport resize. Because it is a custom element, it re-fits automatically
 * whenever the cart drawer morphs new cards in (connectedCallback fires on upgrade).
 */
class FitText extends HTMLElement {
  #scheduled = false;

  connectedCallback() {
    this.style.display = 'block';
    this.style.whiteSpace = 'nowrap';
    this.#schedule();
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.#schedule()).catch(() => {});
    }
    window.addEventListener('resize', this.#onResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
  }

  #onResize = () => this.#schedule();

  #schedule() {
    if (this.#scheduled) return;
    this.#scheduled = true;
    requestAnimationFrame(() => {
      this.#scheduled = false;
      this.#fit();
    });
  }

  #fit() {
    if (!this.isConnected || this.clientWidth === 0) return;
    // Reset to the CSS-defined size, then read it as the ceiling.
    this.style.fontSize = '';
    const max = parseFloat(getComputedStyle(this).fontSize) || 14;
    // Floor low enough that even long names stay on one line; ellipsis is the last resort.
    const min = Math.max(8, max * 0.55);
    let size = max;
    this.style.fontSize = `${size}px`;
    let guard = 0;
    while (this.scrollWidth > this.clientWidth && size > min && guard < 80) {
      size -= 0.5;
      this.style.fontSize = `${size}px`;
      guard += 1;
    }
  }
}

if (!customElements.get('fit-text')) {
  customElements.define('fit-text', FitText);
}
